// The only module that performs side effects against the API and the offline cache;
// everything else in store/ is pure state and derived reads.
//
// Storage split (do not conflate): the schedule DATASET lives in IndexedDB as a
// derived, disposable cache; FAVORITES and My Agenda are user-authored and durable, on
// localStorage.

import { store } from "./app-state.js";
import { conferenceDayForDate } from "./selectors.js";
import { dayKey } from "../lib/datetime.js";
import { getSessions, getSpeakers, getTracks, getRooms, getConference, retry } from "../api/client.js";
import { saveSchedule, readSchedule } from "./offline-cache.js";
import { onNetworkChange } from "../pwa.js";

// Absorbs a brief API unavailability (a cold same-origin /api on first paint, or a
// short prod hiccup) before surfacing an error.
const RETRY = { attempts: 4, baseDelayMs: 250 };

let inFlight = null;

// Load the core dataset into the store, network-first.
// On a network failure it falls back to the IndexedDB offline cache.
// DEDUPED and app-lifetime: concurrent callers join the same in-flight load,
// and the load cannot be cancelled (a view that kicks it then navigates away leaves it
// running to warm the store for whatever mounts next). This is what makes it safe to
// call unconditionally on any view mount.
//
// Contract: load/start is ALWAYS balanced by exactly one terminal
// load/success/load/error, so status is never a lie a mount guard can trip over (this
// is what fixed the cross-view frozen-skeleton). The lone exception is a revalidate,
// which by design dispatches neither the skeleton nor a terminal.
//
// Skeleton-vs-revalidate is decided here from current state, not by a caller flag: an
// already-ready store does a background revalidate (no skeleton, keeps last-known-good
// on failure), otherwise it's an initial load (skeleton, then load/error on failure).
export function loadSchedule(dispatch = store.dispatch) {
  inFlight ??= runLoad(dispatch).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runLoad(dispatch) {
  const revalidate = store.getState().status === "ready";
  if (!revalidate) {
    dispatch({ type: "load/start" });
  }
  try {
    const payload = await retry(() => fetchDataset(), RETRY);
    // Warm the offline cache so a later cold/offline load can recover this dataset.
    void saveSchedule(payload);
    // On the INITIAL load only, open the schedule on today's conference day.
    // Dispatched BEFORE load/success so the first ready paint lands on the right day
    // with no day-switch crossfade (the grid isn't shown while status is still
    // loading, so selecting the day here is invisible). A background revalidate skips
    // this so it can never yank the user off a day they picked by hand.
    if (!revalidate) {
      selectTodayIfInConference(dispatch, payload.sessions);
    }
    dispatch({ type: "load/success", payload });
  } catch (err) {
    if (revalidate) {
      return; // a failed revalidate is invisible: keep the last-known-good data
    }
    dispatch(await recoverFromCache(err));
  }
}

// Select today's conference day if today falls on one, so the schedule opens on the
// day in progress rather than always Day 1. A no-op outside the conference or when the
// day is already selected. Kept out of the reducer so the reducer stays clock-free
// (pure); the clock lives here, at the load seam. `sessions` is passed in because it
// isn't in the store yet when this runs.
function selectTodayIfInConference(dispatch, sessions) {
  const today = conferenceDayForDate(sessions, dayKey(new Date().toISOString()));
  if (today !== null && today !== store.getState().selectedDay) {
    dispatch({ type: "day/select", day: today });
  }
}

async function fetchDataset() {
  const [sessions, tracks, rooms, speakers, conference] = await Promise.all([
    getSessions(),
    getTracks(),
    getRooms(),
    getSpeakers(),
    getConference(),
  ]);
  return { sessions, tracks, rooms, speakers, conference };
}

// Resolve a failed network load to a terminal action. Never throws: a broken cache read
// degrades to load/error rather than skipping the terminal dispatch.
async function recoverFromCache(err) {
  // The offline cache holds the last good dataset; fall back to it before erroring.
  const cached = await readSchedule().catch(() => null);
  if (cached) {
    return { type: "load/success", payload: cached };
  }
  const error = err instanceof Error ? err.message : "Could not load the schedule.";
  return { type: "load/error", error };
}

// Wire the one global reconnect refetch, once, at boot: on coming back online we refresh
// the core dataset so the app self-heals after an offline stretch. One app-lifetime
// watcher (no teardown, no per-view subscription leak), covering every view including
// /agenda. We just call loadSchedule and let it decide skeleton-vs-revalidate from
// state: a stuck error reloads with a skeleton and recovers in place, an already-ready
// store revalidates in the background. We read navigator.onLine SYNCHRONOUSLY for the
// decision rather than trusting the event payload/timing.
export function initData(dispatch = store.dispatch) {
  return onNetworkChange(() => {
    if (!navigator.onLine) {
      return;
    }
    void loadSchedule(dispatch);
  });
}
